const API_BASE_URL = process.env.REACT_APP_API;

// Получаем hostname из переменной окружения
const getHostname = () => {
  return process.env.REACT_APP_HOSTNAME || "dedmoroznavernadke.kapitoliy.ru";
};

// Формируем URL-encoded данные
const encodeFormData = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

// Базовый запрос
const makeRequest = async (endpoint, data) => {
  const formData = encodeFormData({
    ...data,
    hostname: getHostname(),
  });

  // Получаем понятное название операции для сообщений об ошибках
  const getEndpointName = (endpoint) => {
    const names = {
      "/findBooking": "поиск доступных слотов",
      "/auth": "авторизация",
      "/book": "бронирование",
      "/cancel": "отмена бронирования",
    };
    return names[endpoint] || "операция";
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData,
    });

    // Проверяем Content-Type перед парсингом
    const contentType = response.headers.get("content-type");
    let result = {};

    // Получаем текст ответа
    const text = await response.text();

    // Детальное логирование для отладки
    console.log("Response status:", response.status);
    console.log("Response Content-Type:", contentType);
    console.log("Response body length:", text.length);
    console.log("Response body:", text || "(empty)");

    if (!response.ok) {
      // Если ответ пустой при ошибке - это проблема бэкенда
      if (!text || text.trim() === "") {
        console.error(
          "⚠️ Backend returned empty response body for error",
          response.status,
          "on endpoint",
          endpoint
        );

        const endpointName = getEndpointName(endpoint);
        const defaultMessages = {
          400: `Не удалось выполнить ${endpointName}. Сервер не вернул описание ошибки. Пожалуйста, проверьте введенные данные и попробуйте еще раз.`,
          401: `Ошибка авторизации. Сервер не вернул описание ошибки.`,
          403: `Доступ запрещен. Сервер не вернул описание ошибки.`,
          404: `Ресурс не найден. Сервер не вернул описание ошибки.`,
          500: `Внутренняя ошибка сервера. Сервер не вернул описание ошибки. Попробуйте позже.`,
        };
        throw new Error(
          defaultMessages[response.status] ||
            `Ошибка сервера ${response.status} при выполнении ${endpointName}. Сервер не вернул описание ошибки.`
        );
      }

      // Пытаемся распарсить JSON если есть Content-Type
      if (contentType && contentType.includes("application/json")) {
        try {
          result = JSON.parse(text);
        } catch (jsonError) {
          console.error("Failed to parse JSON:", jsonError);
          // Если не JSON, используем текст как ошибку
          throw new Error(text || `Ошибка ${response.status}`);
        }
      } else {
        // Если не JSON, используем текст как ошибку
        result = { error: text };
      }

      // Извлекаем сообщение об ошибке
      const errorMessage =
        result.error ||
        result.message ||
        `Ошибка ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    // Успешный ответ
    if (contentType && contentType.includes("application/json")) {
      try {
        result = text ? JSON.parse(text) : {};
      } catch (jsonError) {
        console.error("Failed to parse JSON response:", jsonError);
        throw new Error("Неверный формат ответа от сервера");
      }
    } else if (text) {
      result = { error: text };
    }

    return result;
  } catch (error) {
    // Обработка сетевых ошибок
    if (error.message === "Failed to fetch" || error.name === "TypeError") {
      console.error("Network error:", error);
      throw new Error(
        "Не удалось подключиться к серверу. Проверьте подключение к интернету и попробуйте еще раз."
      );
    }

    // Если это уже наша ошибка с сообщением, просто пробрасываем её
    if (error.message) {
      throw error;
    }
    // Иначе создаём новую ошибку
    throw new Error(error.message || "Произошла ошибка при выполнении запроса");
  }
};

// Получить последние 10 цифр телефона
export const getMsisdn = (phone) => {
  const digits = phone.replace(/\D/g, "");
  return digits.slice(-10);
};

// Метод отправки SMS и получения свободных слотов
export const findBooking = async (phone) => {
  const msisdn = getMsisdn(phone);
  return await makeRequest("/findBooking", { msisdn });
};

// Метод верификации пользователя
export const auth = async (phone, code) => {
  const msisdn = getMsisdn(phone);
  return await makeRequest("/auth", { msisdn, code });
};

// Метод бронирования слота
export const book = async (phone, meetingTime) => {
  const msisdn = getMsisdn(phone);
  return await makeRequest("/book", { msisdn, meeting_time: meetingTime });
};

// Метод отмены бронирования
export const cancel = async (phone, meetingTime) => {
  const msisdn = getMsisdn(phone);
  const result = await makeRequest("/cancel", {
    msisdn,
    meeting_time: meetingTime,
  });

  // Если result === 3, событие завершено и отмена невозможна
  if (result.result === 3) {
    throw new Error("Событие завершено. Отмена брони невозможна");
  }

  return result;
};
